import api from '../utils/api'

/**
 * 施作照片紀錄 API（高階／品牌方案限定）
 *
 * 後端路由：beauty-server/business/routes/servicePhotoRoutes.js
 * 掛載前綴：/api/businesses
 */
const base = (businessId) => `/api/businesses/${businessId}/service-photos`

// 取得某筆預約的所有照片（店家端，含未對客人開放的）
export const listServicePhotosByBooking = (businessId, bookingId) =>
  api.get(`${base(businessId)}/bookings/${bookingId}`)

/**
 * 批次取得多筆預約的照片摘要（結帳紀錄列表用）。
 *
 * 回傳 data 是以 bookingId 為 key 的物件：
 *   { [bookingId]: { photos: [...前 perBooking 張], total: 全部張數 } }
 * 沒有照片的預約不會出現在 key 裡，前端自己當成 0 張處理。
 *
 * 一次最多 100 筆預約（後端會截斷），perBooking 預設 6。
 */
export const listServicePhotosByBookings = (
  businessId,
  bookingIds,
  perBooking
) =>
  api.get(`${base(businessId)}/bookings`, {
    params: {
      bookingIds: (bookingIds || []).join(','),
      ...(perBooking ? { perBooking } : {})
    }
  })

/**
 * 某位顧客歷次的施作照片，依結帳分組（最新一次在最前面）。
 *
 * params（都可省略）：
 *   page, limit   分頁，以「次」為單位；不帶 limit 回全部
 *   service       只看包含這個服務的施作
 *   dateStart, dateEnd   消費日期區間（YYYY-MM-DD）
 *
 * 回傳 data：
 *   {
 *     customer: { _id, name },
 *     groups: [{ bookingId, checkoutOrderId, orderNo, orderStatus, checkedOut,
 *                date, time, remarks, services, photos }],
 *     total, totalGroups,          // 符合篩選的張數／次數
 *     page, limit, hasMore,
 *     summary: { visits, photos }, // 未篩選的總次數／總張數
 *     serviceOptions: [服務名稱]   // 篩選選單用
 *   }
 * date / time 是結帳單上的牆上時間字串，直接顯示即可。
 */
export const listServicePhotosByCustomer = (businessId, customerId, params) =>
  api.get(`${base(businessId)}/customers/${customerId}`, { params })

/**
 * 上傳照片。
 *
 * files: File[]（一次最多 5 張，後端 multer 限制）
 * options.visibleToCustomer: 整批預設是否給客人看，預設 true
 * options.source: 'checkout' | 'booking'
 *
 * 不要自己指定 Content-Type —— utils/api.js 的攔截器看到 FormData 會把
 * 預設的 application/json 刪掉，讓瀏覽器自己帶 multipart 的 boundary。
 * 手動指定反而可能少了 boundary 而讓後端解析失敗。
 */
export const uploadServicePhotos = (
  businessId,
  bookingId,
  files,
  options = {}
) => {
  const formData = new FormData()
  files.forEach((file) => formData.append('photos', file))

  if (options.visibleToCustomer === false) {
    formData.append('visibleToCustomer', 'false')
  }
  if (options.source) {
    formData.append('source', options.source)
  }

  return api.post(`${base(businessId)}/bookings/${bookingId}`, formData)
}

// 更新單張照片（目前只開放 visibleToCustomer 與 caption）
export const updateServicePhoto = (businessId, photoId, payload) =>
  api.patch(`${base(businessId)}/${photoId}`, payload)

// 刪除單張照片（後端是軟刪除）
export const deleteServicePhoto = (businessId, photoId) =>
  api.delete(`${base(businessId)}/${photoId}`)
